import React, {useEffect, useRef, useState} from 'react';
import {Row, Col} from 'reactstrap';
import {Field} from 'react-final-form';
import Select from "react-select";
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const HIDE_CSV_PROGRAM_REQUEST_IDS = [3, 4, 9];

const FIELD_DEFAULTS = {
    roles:  (options) => options.find(o => o.value === 'Participant'),
    status: (options) => options.find(o =>
        o.value === 'Pending Activation' || o.value === 'pending_activation'
    ),
};

const SetupFields = ({setups, setupsFull, setTypeChanged, typeChanged, savedSettings, csvImportType}) => {

    if (!setups) return null;
    if (!setups.constructor === Object) return null;
    if (Object.keys(setups).length === 0) return null;

    const shouldHide = (formRequest) => {
        if (
            formRequest === 'CSVProgramRequest' &&
            HIDE_CSV_PROGRAM_REQUEST_IDS.includes(csvImportType?.id)
        ) {
            return true;
        }
        return false;
    };

    return (
        <div>
            <h4 className="d-flex align-items-center">
                Setups
                <Tooltip
                    title="Configure additional import settings such as validation rules and field mappings."
                    arrow
                    placement="right"
                >
                    <IconButton size="small" style={{padding: 4, marginLeft: 6}}>
                        <InfoOutlinedIcon style={{fontSize: 18}}/>
                    </IconButton>
                </Tooltip>
            </h4>
            {
                Object.keys(setups).filter(formRequest => !shouldHide(formRequest)).map((formRequest, i) =>
                    <div key={i}>
                        <FormSetupField {...{
                            formRequest,
                            setups,
                            setupsFull,
                            typeChanged,
                            savedSettings,
                            setTypeChanged,
                            csvImportType,
                        }}/>
                    </div>
                )
            }
        </div>
    );
};

export default SetupFields;

const RenderSetupField = ({
    formRequest,
    name,
    rule,
    typeChanged,
    setTypeChanged,
    savedSettings,
    csvImportType,
}) => {

    const [selected, setSelected] = useState(null);
    const inputRef = useRef(null);
    const onChangeRef = useRef(null);

    const onSelectChange = (selectedOption, input) => {
        if (input.name === 'setups[UserRequest][type]') {
            if (selectedOption && typeChanged === false && input.value?.value !== selectedOption.value) {
                setTypeChanged(selectedOption.value);
            }
        }
        setSelected(selectedOption);
        input.onChange(selectedOption);
    };

    useEffect(() => {
        // Clear non-type UserRequest fields when type changes
        if (typeChanged !== false && name !== 'type' && formRequest === 'UserRequest') {
            setSelected(null);
            if (inputRef.current) inputRef.current.clearValue();
            if (onChangeRef.current) onChangeRef.current(null);
        }

        if (typeChanged === false) {
            const matches = rule.match(/mustMatchWith\[([\s\S]*?)\]/);
            if (!matches?.[1]) return;

            const options = matches[1]
                .split('|')
                .map(opt => opt.trim())
                .filter(Boolean)
                .map(opt => ({value: opt, label: opt}));

            // 1. Try to restore from saved settings
            let savedValue = null;
            if (savedSettings && Object.keys(savedSettings).length > 0 && savedSettings['setups']) {
                Object.keys(savedSettings['setups']).forEach((item) => {
                    Object.keys(savedSettings['setups'][item] ?? {}).forEach((subItem) => {
                        if (subItem === name) {
                            savedValue = savedSettings['setups'][item][subItem];
                        }
                    });
                });
            }

            if (savedValue) {
                const found = options.find(o => o.value === savedValue);
                if (found) {
                    setSelected(found);
                    if (onChangeRef.current) onChangeRef.current(found);
                }
                return;
            }

            // 2. No saved value — apply defaults
            let defaultOption = null;

            if (name === 'type' && csvImportType?.name) {
                // Match csvImportType.name against available options
                defaultOption = options.find(o =>
                    o.value === csvImportType.name ||
                    o.value.toLowerCase() === csvImportType.name.toLowerCase()
                );
            } else if (FIELD_DEFAULTS[name]) {
                defaultOption = FIELD_DEFAULTS[name](options);
            }

            if (defaultOption) {
                setSelected(defaultOption);
                if (onChangeRef.current) onChangeRef.current(defaultOption);
            }
        }
    }, [savedSettings, typeChanged]);

    if (rule.indexOf('mustMatchWith') === -1) {
        return <span>:{name}-field</span>;
    }

    const matches = rule.match(/mustMatchWith\[([\s\S]*?)\]/);
    if (!matches || !matches[1]) {
        return <span>:{name}-field</span>;
    }

    const options = matches[1]
        .split('|')
        .filter(Boolean)
        .map(opt => ({value: opt.trim(), label: opt.trim()}));

    return (
        <Field name={`setups[${formRequest}][${name}]`} key={name}>
            {({input, meta}) => {
                onChangeRef.current = input.onChange;
                return (
                    <div>
                        <Select
                            options={options}
                            clearable={false}
                            className="react-select"
                            placeholder={' - - - '}
                            classNamePrefix="react-select"
                            ref={inputRef}
                            value={selected}
                            onChange={(selectedOption) => onSelectChange(selectedOption, input)}
                        />
                        {meta.touched && meta.error &&
                            <span className="form__form-group-error">{meta.error}</span>
                        }
                    </div>
                );
            }}
        </Field>
    );
};

const FormSetupFieldGroup = ({
    formRequest, fields, field, typeChanged, setTypeChanged, setupsFull,
    savedSettings, csvImportType,
}) => {
    const rule = fields[field];
    if (!rule || !rule.includes('mustMatchWith')) return null;

    const setupsSearch = setupsFull[formRequest][field].split('|');
    const isRequired = setupsSearch.filter(item => item === 'nullable').length === 0;

    return (
        <Row
            key={`setupField-${field}`}
            className={field === 'type' ? 'type-row' : ''}
        >
            <Col className='col-md-3'>
                <h5>{field.toUpperCase()}{isRequired ? <b>*</b> : ''}</h5>
            </Col>
            <Col className='col-md-9 col-lg-7 col-xl-5'>
                <RenderSetupField
                    formRequest={formRequest}
                    name={field}
                    rule={fields[field]}
                    typeChanged={typeChanged}
                    setTypeChanged={setTypeChanged}
                    savedSettings={savedSettings}
                    csvImportType={csvImportType}
                />
            </Col>
        </Row>
    );
};

const FormSetupField = ({
    formRequest, setups, setupsFull, typeChanged, setTypeChanged, savedSettings, csvImportType,
}) => {
    return (
        <div>
            <Row>
                <Col md="12"><h5>{formRequest} Setup</h5></Col>
            </Row>
            {
                Object.keys(setups[formRequest]).map((field, j) =>
                    <div key={j}>
                        <FormSetupFieldGroup
                            formRequest={formRequest}
                            fields={setups[formRequest]}
                            field={field}
                            typeChanged={typeChanged}
                            setTypeChanged={setTypeChanged}
                            setupsFull={setupsFull}
                            savedSettings={savedSettings}
                            csvImportType={csvImportType}
                        />
                    </div>
                )
            }
        </div>
    );
};